import { useId, useState } from 'react';
import type { InputHTMLAttributes } from 'react';

import { cx } from '../lib/cx.ts';
import { useFieldState } from './Field.tsx';
import { controlClasses } from './TextInput.tsx';

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'type'> & {
  className?: string;
};

/**
 * A password field with a show/hide toggle.
 *
 * Reveal matters more than it looks on a phone: a long passphrase typed on a
 * touch keyboard is easy to fat-finger, and being unable to check it is why
 * people fall back to short passwords.
 */
export function PasswordInput({ className, ...rest }: PasswordInputProps) {
  const field = useFieldState();
  const [visible, setVisible] = useState(false);
  const statusId = useId();

  return (
    <div className="relative">
      <input
        id={field?.inputId}
        type={visible ? 'text' : 'password'}
        aria-describedby={
          field?.describedBy === undefined ? statusId : `${field.describedBy} ${statusId}`
        }
        aria-invalid={field?.invalid || undefined}
        className={cx(controlClasses, 'pr-24', className)}
        {...rest}
      />

      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        // The button reports what it will do; the live region reports what
        // happened, so a screen reader user is not left guessing.
        className="absolute inset-y-0 right-0 min-h-touch px-3 text-sm font-medium text-muted hover:text-text"
      >
        {visible ? 'Hide' : 'Show'}
      </button>

      <span id={statusId} className="sr-only" aria-live="polite">
        {visible ? 'Password is visible' : 'Password is hidden'}
      </span>
    </div>
  );
}
